import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { DomSanitizer, SafeStyle } from "@angular/platform-browser";
import {
    TCI_CONNECTION_ACTION_TYPE,
    TCI_COMPONENT_MODE,
    ITCIConnection,
    ITCIConnectionRequest,
    ITCIConnectionResponse,
    ITCIConnectionServiceProvider,
    TCITenantConnectionsMenu,
    TCITenantAppCategory,
    TCIConnectionServiceNotificationType,
    TCIConnectionVisibility
} from "@tci/common";
import { NgToolsService } from "../../ng-shared/ng-util/ng-tools.service";

import { TciTenantService } from "../../ng-tenant/tci-tenant.service";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { TciTenantModelService } from "../../ng-tenant/tci-tenant.model";
import { TCITenantMenuServices } from "../../ng-tenant/tci-tenant.menu";
import { NgConnectionsService } from "../ng-connections.service";
import { NgUserService } from "../../ng-shared/ng-services/ng-user.service";
import { NgAppFeatureToggleService } from "../../ng-shared/ng-services/ng-app-feature-toggle.service";
import { BaseConnectionMenuComponentInput } from "../ng-connections-menu";
import { InlineMenuItemHandlerFunction } from "../../ng-tenant/tci-tenant-component";

import { Observable, Subscription } from "rxjs";
import * as lodash from "lodash";
declare var $: any;

@Component({
    selector: "ng-connections-row",
    templateUrl: "./ng-connections-row.component.html"
})
export class NgConnectionsRowComponent implements OnInit, OnDestroy {
    @Input() conn: ITCIConnection;
    @Input() showCategoryCol = true;
    @Input() showVisibilityCol = true;
    @Input() showHamburger = true;
    @Output() Refresh: EventEmitter<null> = new EventEmitter();
    @Output() Delete: EventEmitter<ITCIConnection> = new EventEmitter();

    category: string;
    provider: ITCIConnectionServiceProvider;
    subscriptions: Subscription[] = [];

    _appCount: number;
    appName: Array<string> = [];
    showAppName = false;
    canDelete = false;
    canEdit = false;
    canDeleteTooltip = "";
    visibility: TCIConnectionVisibility = TCIConnectionVisibility.UNKNOWN;

    // Status badge
    showStatusBadge = false;
    statusMessage: string;
    statusMessageTitle: string;
    statusBadgeStyle: SafeStyle;

    // Status menu (HM)
    showStatusMenu = false;
    statusMenuAvailable = false;
    statusMenuOptions: TCITenantConnectionsMenu[] = [];
    statusMenuOptionsInlineFns: { [key: string]: InlineMenuItemHandlerFunction<any, any, any> } = {};

    constructor(
        private sanitize: DomSanitizer,
        private toastrSvc: NgToastrService,
        private tenantSvc: TciTenantService,
        private tenantMenuSrv: TCITenantMenuServices,
        private tenantModelSvc: TciTenantModelService,
        private userSvc: NgUserService,
        private connSvc: NgConnectionsService,
        private tools: NgToolsService,
        private featureToggleSvc: NgAppFeatureToggleService
    ) { }

    ngOnInit() {
        this.category = TCITenantAppCategory.fromConnection(this.conn).label;
        this.connSvc.statusMenuOptions$.subscribe(opts => {
            this.statusMenuOptions = (opts.find(opt => opt.connection.id === this.conn.id) || { comps: [] }).comps;
            this.statusMenuAvailable = this.statusMenuOptions.length > 0;

            // Feed the inline HM items collection
            this.statusMenuOptions
                .forEach(opt => {
                    const selStr = opt.getComponentSelectorType().toString();

                    // Identify inline components
                    const preloadMap = this.tenantMenuSrv.getTenantPreloadMap(this.conn.tenantType);
                    this.statusMenuOptionsInlineFns[selStr] = preloadMap[selStr] && preloadMap[selStr].inlineMenuHandlerFn;
                });
        });
        this.tenantSvc.onInit()
            .subscribe(() => {
                const tenant = this.tenantModelSvc.getTenant(this.conn.tenantType);
                const errMsg = "No connection provider for this connection type: " + this.conn.tenantType.toString();
                if (tenant) {
                    const provider = tenant.getConnectionServiceProvider();
                    if (provider) {
                        this.provider = provider;

                        const input = this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_START_SUBSCRIPTION);
                        this.subscriptions.push(this.provider.startConnectionSubscription(input)
                            .subscribe());

                        this.initSubscriptions()
                            .initStatus();
                    } else {
                        console.warn(errMsg);
                    }
                } else {
                    console.warn(errMsg);
                }
            });
    }

    ngOnDestroy() {
        if (this.provider) {
            this.provider.stopConnectionSubscription(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_STOP_SUBSCRIPTION))
                .subscribe();
        }

        this.subscriptions
            .filter(s => !s.closed)
            .forEach(s => s.unsubscribe());
    }

    rowId(): string {
        return "conn_row_" + this.conn.id;
    }

    initSubscriptions() {
        this.subscriptions.push(
            this.provider.getConnectionsSubject()
                .filter(res => res.connectionId === this.conn.id)
                .subscribe(res => {
                    // console.log(">> Conn Subj Res", this.conn.id, res);

                    switch (res.notificationType) {
                        case TCIConnectionServiceNotificationType.NOTIFICATION_REFRESH:
                            this.initStatus();
                            break;
                    }
                })
        );
        return this;
    }

    initStatus() {
        this.subscriptions.push(
            Observable.combineLatest(
                this.provider.getConnectedAppCount(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_APP_COUNT)),
                this.provider.canDelete(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_DELETE)),
                this.provider.canEdit(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_EDIT)),
                this.provider.getDeleteTooltip(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_DELETE_TOOLTIP)),
                this.provider.hasConnectionStatus(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_STATUS)),
                this.provider.getConnectionStatus(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_STATUS)),
                this.provider.showConnectedApps(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_APP_NAME)),
                this.provider.getConnectedApps(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_APP_NAME)),
                (appCount, canDelete, canEdit, canDeleteTooltip, hasStatus, status, showAppName, appName) =>
                    ({ appCount, canDelete, canEdit, canDeleteTooltip, hasStatus, status, showAppName, appName })
            ).subscribe(res => {
                this._appCount = res.appCount;
                this.canDelete = res.canDelete;
                this.canEdit = res.canEdit;
                this.showAppName = res.showAppName;
                this.appName = res.appName ? res.appName.join(", ") : "";
                if (res.canDeleteTooltip && res.canDeleteTooltip.length > 0) {
                    this.canDeleteTooltip = res.canDeleteTooltip;
                } else {
                    this.canDeleteTooltip = "Delete connection";
                }

                this.showStatusBadge = res.hasStatus;
                this.getStatusBadgeStyle(res.status);
            })
        );

        return this;
    }

    getConnectionRequest(actionType: TCI_CONNECTION_ACTION_TYPE): ITCIConnectionRequest {
        return {
            connection: this.conn,
            actionType
        };
    }

    edit() {
        const returnUrl = "connections";
        this.provider.editConnection(this.getConnectionRequest(TCI_CONNECTION_ACTION_TYPE.CONN_EDIT), returnUrl)
            .subscribe(editRes => {
                if (editRes.error) {
                    this.toastrSvc.error("The connection was not able to be edited", "for " + this.conn.name);
                    console.warn("Connection Edition returned error", editRes);
                } else if (editRes.notificationType === TCIConnectionServiceNotificationType.NOTIFICATION_REFRESH) {
                    this.Refresh.emit();
                }
            });
    }

    delete($event: MouseEvent) {
        if (this.canDelete) {
            this.Delete.emit(this.conn);
        }
        $event.stopPropagation();
    }

    get appCount(): string {
        if (lodash.isUndefined(this._appCount)) {
            return "";
        } else if (this._appCount === 1) {
            return "1 app";
        } else {
            return this._appCount + " apps";
        }
    }

    toggleStatusMenu($event: MouseEvent) {
        this.showStatusMenu = !this.showStatusMenu;
        $event.stopPropagation();
    }

    emitTenantOutput(event) {
        //
    }

    private getStatusBadgeStyle(status: ITCIConnectionResponse): void {
        if (status && status.connectionStatus) {
            this.statusBadgeStyle = this.sanitize
                .bypassSecurityTrustStyle(`background-image: url("${status.connectionStatus.getIconUrl()}")`);
            this.statusMessage = status.connectionStatus.getToolTip();
            this.statusMessageTitle = status.connectionStatus.getTitle();
        } else {
            this.statusBadgeStyle = null;
            this.statusMessage = "";
            this.statusMessageTitle = "";
        }

        this.initTooltipWithHeader();
    }

    private initTooltipWithHeader() {
        // This first setTimeout is waiting for connection list to be rendered
        this.tools.setTimeout(() => {
            $(".conn-status-badge").mouseover((e) => {
                const badgeDomPositionTop = $(e.target).offset().top;
                const badgeDomHeight = $(e.target).height();
                const messageTitle = $(e.target).attr("title");
                // This second setTimeout is waiting for ng-tooltip html dom to be rendered
                this.tools.setTimeout(() => {
                    // show title in tooltip only when title is not empty
                    const tooltipDom = $(".ui-tooltip.connections-row");
                    if (messageTitle) {
                        // see client/src/app/ng-connections/ng-connections.component.less line 319, content: attr(title);
                        // put below code inside setTimeout, that is because ng-tooltip html dom has not been created
                        // wait for the dom to be created, then set title attribute
                        tooltipDom.find(".ui-tooltip-text.ui-corner-all").attr("title", messageTitle);

                        // This third setTimeout is waiting for ng-tooltip title to be rendered
                        this.tools.setTimeout(() => {
                            // reset tooltip position after add tooltip title
                            tooltipDom.css("top", (badgeDomPositionTop - tooltipDom.outerHeight() / 2 + badgeDomHeight / 2) + "px");
                        });
                    }
                });
            });
        });
    }

    executeInlineCompFn($event: MouseEvent, opt: TCITenantConnectionsMenu) {
        const selector = opt.getComponentSelectorType();
        const selStr = selector.toString();
        const tenantHMInput = new BaseConnectionMenuComponentInput(
            this.conn.tenantType,
            selector,
            this.conn,
            this.userSvc,
            this.featureToggleSvc,
            TCI_COMPONENT_MODE.COMP_MODE_CONN_HAMBURGER,
            opt,
            null
        );
        const injector = this.tenantMenuSrv.getTenantInjector(this.conn.tenantType);
        this.statusMenuOptionsInlineFns[selStr](opt, tenantHMInput, injector).subscribe();
        this.toggleStatusMenu($event);
    }
}
